import { Injectable, EventEmitter } from "@angular/core";
import { HttpClient, HttpHeaders, HttpResponse } from "@angular/common/http";
import { Observable, of, throwError, forkJoin } from "rxjs";
import { delay, map, switchMap, take } from "rxjs/operators";
import { of as observableOf, BehaviorSubject, Subject } from "rxjs";
import { environment } from "../environments/environment";
import { StorageService, StorageKeys } from "./storage.service";
import { WindowService } from "./window.service";
import { LogService } from "./log.service";
import moment from "moment";
import { HelperService } from "./helper.service";
import { UserService } from "./user.service";
import { format } from "date-fns";
import { Order, OrderLineItem, OrderFulfillment, OrderImage } from "../models";

// TypeScript interfaces for Fender Shopify orders
export interface FenderShopifyTaxLine {
  rate: number;
  title: string;
  priceSet: {
    amount: string;
    currency: string;
  };
}

export interface FenderShopifyLineItem {
  id: string;
  title: string;
  quantity: number;
  image: {
    url: string;
    src: string;
  };
  name: string;
  sku: string;
  variantTitle: string;
  originalTotal: {
    amount: string;
    currency: string;
  };
  discountedTotal: {
    amount: string;
    currency: string;
  };
  taxLines: FenderShopifyTaxLine[];
}

export interface FenderShopifyTrackingInfo {
  company: string;
  number: string;
  url: string;
}

export interface FenderShopifyFulfillment {
  status: string;
  name: string;
  createdAt: string;
  deliveredAt: string | null;
  displayStatus: string;
  estimatedDeliveryAt: string | null;
  inTransitAt: string | null;
  updatedAt: string;
  trackingInfo: FenderShopifyTrackingInfo[];
}

export interface FenderShopifyOrder {
  id: string;
  name: string;
  note: string | null;
  email: string;
  phone: string | null;
  paymentGatewayNames: string[];
  processedAt: string;
  tags: string[];
  fullyPaid: boolean;
  createdAt: string;
  totalPrice: {
    amount: string;
    currency: string;
  };
  fulfillable: boolean;
  fulfillments: FenderShopifyFulfillment[];
  lineItems: FenderShopifyLineItem[];
  description: string;
  billingAddress?: {
    firstName?: string;
    lastName?: string;
    address1?: string;
    address2?: string | null;
    city?: string;
    province?: string;
    country?: string;
    zip?: string;
    phone?: string | null;
  };
  shippingAddress?: {
    firstName?: string;
    lastName?: string;
    address1?: string;
    address2?: string | null;
    city?: string;
    province?: string;
    country?: string;
    zip?: string;
    phone?: string | null;
  };
  currentShippingPriceSet?: {
    shopMoney: {
      amount: string;
      currencyCode: string;
    };
  };
  currentTotalDiscountsSet?: {
    shopMoney: {
      amount: string;
      currencyCode: string;
    };
  };
  discountCodes?: string[];
}

export interface FenderUserShopAddress {
  id: string;
  firstName: string;
  lastName: string;
  address1: string;
  address2: string;
  city: string;
  country: string;
  zip: string;
  phone: string;
  name: string;
}

export interface FenderUserShopData {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  multipassIdentifier: string;
  addresses: FenderUserShopAddress[];
  createdAt: string;
  updatedAt: string;
  numberOfOrders: string;
  state: string;
  amountSpent: {
    amount: string;
    currencyCode: string;
  };
  verifiedEmail: boolean;
  taxExempt: boolean;
  tags: string[];
  defaultAddress: FenderUserShopAddress;
  storeId: string;
  storeUrl: string;
  ssoStoreUrl: string;
  ssoStoreId: string;
  shopifyId: string;
}

export interface FenderUserShopAddress {
  id: string;
  firstName: string;
  lastName: string;
  address1: string;
  address2: string;
  city: string;
  country: string;
  zip: string;
  phone: string;
  name: string;
}

export interface FenderUserShopData {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  multipassIdentifier: string;
  addresses: FenderUserShopAddress[];
  createdAt: string;
  updatedAt: string;
  numberOfOrders: string;
  state: string;
  amountSpent: {
    amount: string;
    currencyCode: string;
  };
  verifiedEmail: boolean;
  taxExempt: boolean;
  tags: string[];
  defaultAddress: FenderUserShopAddress;
  storeId: string;
  storeUrl: string;
  ssoStoreUrl: string;
  ssoStoreId: string;
  shopifyId: string;
}
@Injectable({
  providedIn: "root",
})
export class OrderService {
  constructor(
    private http: HttpClient,
    private storage: StorageService,
    private window: WindowService,
    private _log: LogService,
    private _helpers: HelperService,
    public userService: UserService
  ) {
    this._helpers.userID = this.userService.user ? this.userService.user.id : undefined;
  }

  public fenderUserShopData: FenderUserShopData;

  public shopOrderPlaced$: EventEmitter<string> = new EventEmitter();

  private _orders: any;
  get orders() {
    if (!this._orders) this._orders = this.storage.getItem(StorageKeys.ORDERS);
    return this._orders;
  }

  set orders(orders) {
    this._orders = orders;
    this.storage.setItem(StorageKeys.ORDERS, orders);
  }

  public orders$: Subject<any> = new Subject();
  public recentOrders$: Subject<any> = new Subject();
  getOrders(fresh?, recent?) {
    let cachedOrders = this.storage.getItem(StorageKeys.ORDERS);
    if (!fresh && this.storage.checkTimestamp("orders") && cachedOrders) {
      return observableOf(cachedOrders);
    } else {
      return this.http.get(environment.paeApiUrl + `user/orders`, this._helpers.getHttpOptions()).pipe(
        map((result: any) => {
          this.storage.setTimestamp("orders", 1);
          this.storage.setItem(StorageKeys.ORDERS, result);
          return result;
        })
      );
    }
  }

  /** getOrder() Returns a cached order detail. */
  getOrder(id) {
    var order = this.orders.filter((o) => o.id == id)[0];
    return order;
  }

  getFenderShopifyAccountDetails(email, force?, siteID?) {
    let storedShopifyAccountDetails = this.storage.getItem(StorageKeys.FENDER_SHOPIFY_ACCOUNT_DETAILS);
    if (!force && this.storage.checkTimestamp("fender_shopify_account_details") && storedShopifyAccountDetails) {
      this.fenderUserShopData = storedShopifyAccountDetails;
      return observableOf(this.fenderUserShopData);
    } else {
      let args = {
        "Content-Type": "application/json",
        "x-api-key": environment.fenderShopify.fenderOrdersApiKey,
      };
      let headers = { headers: new HttpHeaders(args) };
      if (!siteID) siteID = "PRESONUS_US";
      return this.http
        .post(
          environment.fenderShopify.fenderOrdersApiUrl,
          {
            requestType: "getCustomerStoreData",
            siteID: siteID,
            data: {
              customerQuery: {
                email: email,
              },
            },
          },
          headers
        )
        .pipe(
          map((result: any) => {
            if (result?.data?.id) {
              this.fenderUserShopData = result?.data;
              this.fenderUserShopData.shopifyId = result.data.id.split("gid://shopify/Customer/")[1];
            }
            if (this.fenderUserShopData) {
              this.storage.setTimestamp("fender_shopify_account_details", 72);
              this.storage.setItem(StorageKeys.FENDER_SHOPIFY_ACCOUNT_DETAILS, this.fenderUserShopData);
            }
            return this.fenderUserShopData;
          })
        );
    }
  }

  updateFenderShopifyUser(
    email: string,
    siteID?: string,
    data?: {
      firstName?: string;
      lastName?: string;
      email?: string;
    }
  ) {
    let args = {
      "Content-Type": "application/json",
      "x-api-key": environment.fenderShopify.fenderOrdersApiKey,
    };
    let headers = { headers: new HttpHeaders(args) };
    if (!siteID) siteID = "PRESONUS_US";
    return this.http
      .post(
        environment.fenderShopify.fenderOrdersApiUrl,
        {
          requestType: "updateCustomerDataWithoutShopifyIDOrStoreID",
          siteID: siteID,
          data: {
            customerQuery: {
              email: email,
            },
            customerData: data,
          },
        },
        headers
      )
      .pipe(
        delay(1000),
        switchMap((result: any) => {
          return this.getFenderShopifyAccountDetails(data.email, true, siteID);
        })
      );
  }

  getFenderShopifyOrders(shopifyId: string, storeId?: string): Observable<FenderShopifyOrder[]> {
    if (!storeId) storeId = this.fenderUserShopData.storeId;
    let args = {
      "Content-Type": "application/json",
      "x-api-key": environment.fenderShopify.fenderOrdersApiKey,
    };
    let headers = { headers: new HttpHeaders(args) };
    return this.http
      .post(
        environment.fenderShopify.fenderOrdersApiUrl,
        {
          requestType: "getCustomerOrders",
          siteID: storeId,
          data: {
            customerQuery: shopifyId,
          },
        },
        headers
      )
      .pipe(
        map((result: any) => {
          console.log("getFenderShopifyOrders", result);
          return this.parseFenderShopifyOrders(result);
        })
      );
  }

  public ordersById: any = {};
  getAllUserOrders(email: string, fresh?: boolean, siteID?: string): Observable<Order[]> {
    return this.getFenderShopifyAccountDetails(email, fresh, siteID).pipe(
      switchMap((accountDetails: FenderUserShopData) => {
        if (!accountDetails?.shopifyId) {
          // If no Shopify account exists, just return regular orders
          return this.getOrders(fresh).pipe(map((orders) => this.transformPresonusOrderData(orders)));
        }

        // Call both order methods in parallel
        return forkJoin({
          orders: this.getOrders(fresh),
          fenderShopifyOrders: this.getFenderShopifyOrders(accountDetails.shopifyId, accountDetails.storeId),
        }).pipe(
          map((result) => {
            const presonusOrders = this.transformPresonusOrderData(result.orders);
            const fenderOrders = this.transformFenderOrderData(result.fenderShopifyOrders);
            // Combine both arrays into one
            return [...presonusOrders, ...fenderOrders];
          })
        );
      })
    );
  }

  transformPresonusOrderData(orders: any[]): Order[] {
    // Filter out recurring orders
    const filteredOrders = orders.filter((order) => order.type !== "recurring");

    return filteredOrders.map((order) => {
      const payload = order.order_ipn?.payload;
      if (!payload) {
        // Fallback for orders without Shopify payload
        return this.transformLegacyPresonusOrder(order);
      }

      // Transform line items
      const items: OrderLineItem[] =
        payload.line_items?.map((item: any) => ({
          id: item.id,
          title: item.title,
          variantTitle: item.variant_title,
          quantity: item.quantity,
          price: parseFloat(item.price),
          total: parseFloat(item.price) * item.quantity,
          sku: item.sku,
          image: item.image ? { url: item.image } : undefined,
          taxAmount: item.tax_lines?.reduce((sum: number, tax: any) => sum + parseFloat(tax.price), 0) || 0,
          discountAmount: parseFloat(item.total_discount || 0),
        })) || [];

      // Transform fulfillments
      const fulfillments: OrderFulfillment[] =
        payload.fulfillments?.map((f: any) => ({
          id: f.id,
          name: f.name,
          status: f.status,
          displayStatus: f.shipment_status || f.status,
          createdAt: f.created_at,
          deliveredAt: f.delivered_at || null,
          trackingCompany: f.tracking_company,
          trackingNumber: f.tracking_number,
          trackingUrl: f.tracking_url,
          trackingInfo:
            f.tracking_numbers?.map((num: string, idx: number) => ({
              company: f.tracking_company,
              number: num,
              url: f.tracking_urls?.[idx],
            })) || [],
        })) || [];

      // Generate description from line items
      const description = items.map((item) => `${item.title} (${item.quantity})`).join(", ");

      const transformedOrder: Order = {
        id: order.idorder_history || order.id,
        name: payload.name,
        orderNumber: payload.order_number,
        source: "presonus",
        shopifyId: payload.id ? `gid://shopify/Order/${payload.id}` : undefined,
        date: order.order_date || payload.created_at,
        email: payload.email,

        // Financial details
        subtotal: parseFloat(payload.subtotal_price || 0),
        total: parseFloat(payload.total_price || 0),
        tax: parseFloat(payload.total_tax || 0),
        discount: parseFloat(payload.total_discounts || 0),
        shipping: parseFloat(payload.total_shipping_price_set?.shop_money?.amount || 0),
        currency: payload.currency || "USD",

        // Status
        paymentStatus: payload.financial_status || "unknown",
        fulfillmentStatus: payload.fulfillment_status || "unfulfilled",

        // Items and fulfillments
        items: items,
        fulfillments: fulfillments,

        // Additional details
        description: description,
        note: payload.note,
        tags: payload.tags?.split?.(", ") || payload.tags || [],
      };

      this.ordersById[transformedOrder.id] = transformedOrder;
      return transformedOrder;
    });
  }

  transformLegacyPresonusOrder(order: any): Order {
    // Handle older PreSonus orders that don't have Shopify payload
    const items = order.orderItems || order.details || [];
    const description = items.length > 0 ? items.map((item: any) => `${item.title || item.product_key || "Item"}`).join(", ") : undefined;
    let legacyOrder: Order = {
      id: order.idorder_history || order.id,
      name: order.reference_number,
      source: "presonus",
      date: order.order_date,
      email: order.email,

      subtotal: 0,
      total: order.total || 0,
      tax: 0,
      discount: 0,
      shipping: 0,
      currency: "USD",

      paymentStatus: order.paymentStatus || "paid",
      fulfillmentStatus: "fulfilled",

      items: items,
      fulfillments: [],

      description: description,
      tags: [],
    };
    this.ordersById[legacyOrder.id] = legacyOrder;
    return legacyOrder;
  }

  transformFenderOrderData(orders: any[]): Order[] {
    return orders.map((order) => {
      // Transform line items
      console.log(order);
      const items: OrderLineItem[] =
        order.lineItems?.map((item: any) => ({
          id: item.id?.replace?.("gid://shopify/LineItem/", "") || item.id,
          title: item.title,
          variantTitle: item.variantTitle,
          quantity: item.quantity,
          price: parseFloat(item.originalTotal?.amount || 0),
          total: parseFloat(item.discountedTotal?.amount || item.originalTotal?.amount || 0),
          sku: item.sku,
          image: item.image?.url ? { url: item.image.url, alt: item.title } : undefined,
          taxAmount: item.taxLines?.reduce((sum: number, tax: any) => sum + parseFloat(tax.priceSet?.amount || 0), 0) || 0,
          discountAmount: parseFloat(item.originalTotal?.amount || 0) - parseFloat(item.discountedTotal?.amount || 0),
        })) || [];

      // Transform fulfillments
      const fulfillments: OrderFulfillment[] =
        order.fulfillments?.map((f: any) => ({
          id: f.id,
          name: f.name,
          status: f.status,
          displayStatus: f.displayStatus,
          createdAt: f.createdAt,
          deliveredAt: f.deliveredAt,
          estimatedDeliveryAt: f.estimatedDeliveryAt,
          trackingInfo: f.trackingInfo || [],
        })) || [];

      // Calculate financial details
      const subtotal = items.reduce((sum, item) => sum + item.total, 0);
      const tax = items.reduce((sum, item) => sum + (item.taxAmount || 0), 0);
      // Use total discounts from order level if available, otherwise calculate from line items
      const discount = order.currentTotalDiscountsSet?.shopMoney?.amount
        ? parseFloat(order.currentTotalDiscountsSet.shopMoney.amount)
        : items.reduce((sum, item) => sum + (item.discountAmount || 0), 0);
      const total = parseFloat(order.totalPrice?.amount || 0);
      // Use shipping cost from order level
      const shipping = order.currentShippingPriceSet?.shopMoney?.amount ? parseFloat(order.currentShippingPriceSet.shopMoney.amount) : 0;

      // Use existing description or generate from line items
      const description = order.description || items.map((item) => `${item.title} (${item.quantity})`).join(", ");

      const transformedOrder: Order = {
        id: order.id?.replace?.("gid://shopify/Order/", "") || order.id,
        name: order.name,
        source: "fender",
        shopifyId: order.shopifyId || order.id,
        date: order.createdAt || order.processedAt,
        email: order.email,

        // Financial details
        subtotal: subtotal,
        total: total,
        tax: tax,
        discount: discount,
        shipping: shipping,
        currency: order.totalPrice?.currency || order.currentShippingPriceSet?.shopMoney?.currencyCode || "USD",

        // Status
        paymentStatus: order.fullyPaid ? "paid" : "pending",
        fulfillmentStatus: order.fulfillments?.length > 0 ? "fulfilled" : order.fulfillable ? "unfulfilled" : "not_required",

        // Items and fulfillments
        items: items,
        fulfillments: fulfillments,

        // Addresses
        billingAddress: order.billingAddress
          ? {
              firstName: order.billingAddress.firstName,
              lastName: order.billingAddress.lastName,
              address1: order.billingAddress.address1,
              address2: order.billingAddress.address2,
              city: order.billingAddress.city,
              province: order.billingAddress.province,
              country: order.billingAddress.country,
              zip: order.billingAddress.zip,
              phone: order.billingAddress.phone,
            }
          : undefined,
        shippingAddress: order.shippingAddress
          ? {
              firstName: order.shippingAddress.firstName,
              lastName: order.shippingAddress.lastName,
              address1: order.shippingAddress.address1,
              address2: order.shippingAddress.address2,
              city: order.shippingAddress.city,
              province: order.shippingAddress.province,
              country: order.shippingAddress.country,
              zip: order.shippingAddress.zip,
              phone: order.shippingAddress.phone,
            }
          : undefined,

        // Discount codes
        discountCodes: order.discountCodes || [],

        // Additional details
        description: description,
        note: order.note,
        tags: order.tags || [],
      };

      this.ordersById[transformedOrder.id] = transformedOrder;
      return transformedOrder;
    });
  }

  getFenderGiftCards(shopifyId: string, storeId?: string): Observable<any> {
    if (!storeId) storeId = this.fenderUserShopData.storeId;
    let args = {
      "Content-Type": "application/json",
      "x-api-key": environment.fenderShopify.fenderOrdersApiKey,
    };
    let headers = { headers: new HttpHeaders(args) };
    return this.http
      .post(
        environment.fenderShopify.fenderOrdersApiUrl,
        {
          requestType: "getCustomerGiftCards",
          siteID: storeId,
          data: {
            customerQuery: shopifyId,
          },
        },
        headers
      )
      .pipe(
        map((result: any) => {
          return this.parseFenderShopifyGiftCards(result);
        })
      );
  }

  addUpdateShopifyAddress(
    shopifyId: any,
    data: {
      address1: string;
      address2: string;
      city: string;
      company: string;
      countryCode: string;
      provinceCode: string;
      firstName: string;
      lastName: string;
      phone: string;
      zip: string;
    },
    addressId?: any,
    storeId?: any
  ): Observable<any> {
    if (!storeId) storeId = this.fenderUserShopData.storeId;
    let args = {
      "Content-Type": "application/json",
      "x-api-key": environment.fenderShopify.fenderOrdersApiKey,
    };
    let headers = { headers: new HttpHeaders(args) };
    return this.http
      .post(
        environment.fenderShopify.fenderOrdersApiUrl,
        {
          requestType: "createUpdateDeleteCustomerAddress",
          siteID: storeId,
          data: {
            customerQuery: shopifyId,
            customerAddressFunction: addressId ? "update" : "create",
            ...(addressId && { addressId }),
            addressData: data,
          },
        },
        headers
      )
      .pipe(
        delay(1000),
        switchMap((result: any) => {
          return this.getFenderShopifyAccountDetails(this.userService.user.email, true, storeId);
        })
      );
  }

  deleteShopifyAddress(shopifyId: any, addressId: any, storeId?: any): Observable<any> {
    if (!storeId) storeId = this.fenderUserShopData.storeId;
    let args = {
      "Content-Type": "application/json",
      "x-api-key": environment.fenderShopify.fenderOrdersApiKey,
    };
    let headers = { headers: new HttpHeaders(args) };
    return this.http
      .post(
        environment.fenderShopify.fenderOrdersApiUrl,
        {
          requestType: "createUpdateDeleteCustomerAddress",
          siteID: storeId,
          data: {
            customerQuery: shopifyId,
            customerAddressFunction: "delete",
            addressId: addressId,
          },
        },
        headers
      )
      .pipe(
        delay(1000),
        switchMap((result: any) => {
          return this.getFenderShopifyAccountDetails(this.userService.user.email, true, storeId);
        })
      );
  }

  parseFenderShopifyGiftCards(rawData: any): any[] {
    if (!rawData?.data?.getCustomerGiftCardsResponse) {
      return [];
    }
    return rawData.data.getCustomerGiftCardsResponse.map((giftCardWrapper: any) => {
      let giftCard: any = {};
      giftCard.id = giftCardWrapper.node.id;
      giftCard.balance = parseFloat(giftCardWrapper.node.balance.amount).toFixed(2) + " " + giftCardWrapper.node.balance.currencyCode;
      giftCard.code = giftCardWrapper.node.maskedCode;
      giftCard.expires = giftCardWrapper.node.expiresOn ? format(giftCardWrapper.node.expiresOn, "MM/dd/yyyy") : "Never";
      return giftCard;
    });
  }

  /**
   * Parses the raw Shopify order data into a more readable format
   * @param rawData The raw response from getFenderShopifyOrders
   * @returns Parsed order data with simplified structure
   */
  parseFenderShopifyOrders(rawData: any): FenderShopifyOrder[] {
    if (!rawData?.data?.getCustomerOrdersResponse) {
      return [];
    }

    return rawData.data.getCustomerOrdersResponse.map((orderWrapper: any) => {
      const order = orderWrapper.node;
      const lineItems = order.lineItems.edges.map((itemWrapper: any) => ({
        id: itemWrapper.node.id,
        title: itemWrapper.node.title,
        quantity: itemWrapper.node.quantity,
        image: {
          url: itemWrapper.node.image?.url,
          src: itemWrapper.node.image?.src,
        },
        name: itemWrapper.node.name,
        sku: itemWrapper.node.sku,
        variantTitle: itemWrapper.node.variantTitle,
        originalTotal: {
          amount: itemWrapper.node.originalTotalSet.shopMoney.amount,
          currency: itemWrapper.node.originalTotalSet.shopMoney.currencyCode,
        },
        discountedTotal: {
          amount: itemWrapper.node.discountedTotalSet.shopMoney.amount,
          currency: itemWrapper.node.discountedTotalSet.shopMoney.currencyCode,
        },
        taxLines: itemWrapper.node.taxLines.map((tax: any) => ({
          rate: tax.rate,
          title: tax.title,
          priceSet: {
            amount: tax.priceSet.shopMoney.amount,
            currency: tax.priceSet.shopMoney.currencyCode,
          },
        })),
      }));

      // Create description from product titles
      const description = lineItems.map((item) => `${item.title} (${item.quantity})`).join(", ");

      // Parse fulfillments with proper structure
      const fulfillments = order.fulfillments.map((fulfillment: any) => ({
        status: fulfillment.status,
        name: fulfillment.name,
        createdAt: fulfillment.createdAt,
        deliveredAt: fulfillment.deliveredAt,
        displayStatus: fulfillment.displayStatus,
        estimatedDeliveryAt: fulfillment.estimatedDeliveryAt,
        inTransitAt: fulfillment.inTransitAt,
        updatedAt: fulfillment.updatedAt,
        trackingInfo: fulfillment.trackingInfo.map((tracking: any) => ({
          company: tracking.company,
          number: tracking.number,
          url: tracking.url,
        })),
      }));

      return {
        id: order.id,
        name: order.name,
        note: order.note,
        email: order.email,
        phone: order.phone,
        paymentGatewayNames: order.paymentGatewayNames,
        processedAt: order.processedAt,
        tags: order.tags,
        fullyPaid: order.fullyPaid,
        createdAt: order.createdAt,
        totalPrice: {
          amount: order.totalPriceSet.shopMoney.amount,
          currency: order.totalPriceSet.shopMoney.currencyCode,
        },
        fulfillable: order.fulfillable,
        fulfillments: fulfillments,
        lineItems: lineItems,
        description: description,
        billingAddress: order.billingAddress
          ? {
              firstName: order.billingAddress.firstName,
              lastName: order.billingAddress.lastName,
              address1: order.billingAddress.address1,
              address2: order.billingAddress.address2,
              city: order.billingAddress.city,
              province: order.billingAddress.province,
              country: order.billingAddress.country,
              zip: order.billingAddress.zip,
              phone: order.billingAddress.phone,
            }
          : undefined,
        shippingAddress: order.shippingAddress
          ? {
              firstName: order.shippingAddress.firstName,
              lastName: order.shippingAddress.lastName,
              address1: order.shippingAddress.address1,
              address2: order.shippingAddress.address2,
              city: order.shippingAddress.city,
              province: order.shippingAddress.province,
              country: order.shippingAddress.country,
              zip: order.shippingAddress.zip,
              phone: order.shippingAddress.phone,
            }
          : undefined,
        currentShippingPriceSet: order.currentShippingPriceSet
          ? {
              shopMoney: {
                amount: order.currentShippingPriceSet.shopMoney.amount,
                currencyCode: order.currentShippingPriceSet.shopMoney.currencyCode,
              },
            }
          : undefined,
        currentTotalDiscountsSet: order.currentTotalDiscountsSet
          ? {
              shopMoney: {
                amount: order.currentTotalDiscountsSet.shopMoney.amount,
                currencyCode: order.currentTotalDiscountsSet.shopMoney.currencyCode,
              },
            }
          : undefined,
        discountCodes: order.discountCodes || [],
      };
    });
  }

  clearCache() {
    // this.orders$.next(false);
    // this.recentOrders$.next(false);
    this._orders = false;
    this.storage.removeItem(StorageKeys.ORDERS);
  }
}
